import { IconButton, InputAdornment, TextField, TextFieldProps, Typography } from '@mui/material';
import React from 'react';

import { TextFieldWrapper } from './text-field-styles';
import { TextFieldTitle } from './TextFieldTitle';

interface IProps extends Omit<TextFieldProps, 'variant' | 'size'> {
    id?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    variant?: 'outlined' | 'filled' | 'standard';
    disabled?: boolean;
    required?: boolean;
    type?: string;
    placeholder?: string;
    error?: boolean;
    name?: string;
    value?: string | number;
    size?: 'medium' | 'small';
    isAutoFocus?: boolean;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    maxLength?: number;
    className?: string;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    errorValue?: string;
    title?: string;
    helperTitle?: string;
    helperTitleOnClick?: () => void;
    sx?: Record<string, unknown>;
    autoComplete?: string;
    preventCopy?: boolean; // Prevent copy/paste operations
    showPasswordToggle?: boolean; // Show password toggle icon
    onToggleVisibility?: (visible: boolean) => void; // Callback for visibility toggle
    label?: string;
    multiline?: boolean;
    rows?: number;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void; // Allow custom onKeyDown handler
    onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void; // Allow custom onPaste handler (overrides preventCopy behavior)
    inputMode?: 'decimal' | 'numeric' | 'text' | 'email' | 'tel' | 'url' | 'search'; // HTML inputMode attribute
    ariaLabel?: string; // Accessible label for screen readers (use when label is visually hidden)
    inputRef?: React.Ref<HTMLInputElement>;
}

export const TextFieldComponent: React.FC<IProps> = ({
    id,
    onChange,
    variant = 'outlined',
    disabled = false,
    required = false,
    type = 'text',
    placeholder = '',
    error = false,
    errorValue = '',
    name = '',
    value = '',
    size = 'medium',
    isAutoFocus = false,
    onBlur,
    maxLength,
    sx,
    startIcon,
    endIcon,
    title, // new prop
    helperTitle,
    helperTitleOnClick,
    autoComplete,
    preventCopy = false,
    showPasswordToggle = false,
    onToggleVisibility,
    label,
    multiline = false,
    rows,
    onKeyDown,
    onPaste: customOnPaste,
    inputMode,
    ariaLabel,
    className,
    inputRef,
}) => {
    const generatedId = React.useId();
    const inputId = id || `textfield-${generatedId.replace(/:/g, '')}`;
    const errorId = `${inputId}-error`;
    const [showPassword, setShowPassword] = React.useState(false);

    const handleShowPassword = () => {
        setShowPassword(true);
        if (onToggleVisibility) {
            onToggleVisibility(true);
        }
    };

    const handleHidePassword = () => {
        setShowPassword(false);
        if (onToggleVisibility) {
            onToggleVisibility(false);
        }
    };

    // Prevent copy, paste, and cut operations
    const handleCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (preventCopy) {
            e.preventDefault();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        // If custom onPaste handler is provided, use it instead
        if (customOnPaste) {
            customOnPaste(e);
            return;
        }
        // Otherwise, use preventCopy behavior
        if (preventCopy) {
            e.preventDefault();
        }
    };

    const handleCut = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (preventCopy) {
            e.preventDefault();
        }
    };

    const renderEndAdornment = () => {
        // Show password toggle if type is password or showPasswordToggle is true
        if (type === 'password' || showPasswordToggle) {
            return (
                <InputAdornment position="end">
                    <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onMouseDown={handleShowPassword}
                        onMouseUp={handleHidePassword}
                        onMouseLeave={handleHidePassword}
                        onTouchStart={handleShowPassword}
                        onTouchEnd={handleHidePassword}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowPassword(prev => !prev);

                                if (onToggleVisibility) {
                                    onToggleVisibility(!showPassword);
                                }
                            }
                        }}
                        edge="end"
                    >
                        {showPassword ? (
                            <span className="icon-eye" style={{ fontSize: 16 }} />
                        ) : (
                            <span className="icon-eye-off" style={{ fontSize: 16 }} />
                        )}
                    </IconButton>
                </InputAdornment>
            );
        }

        if (endIcon) {
            return <InputAdornment position="end">{endIcon}</InputAdornment>;
        }

        return null;
    };

    const renderStartAdornment = () => {
        if (startIcon) {
            return <InputAdornment position="start">{startIcon}</InputAdornment>;
        }
        return null;
    };
    // Custom onChange to trim and prevent whitespace for password fields
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (type === 'password' || showPasswordToggle) {
            // Remove all whitespace and trim
            const trimmed = e.target.value.replace(/\s/g, '');
            if (onChange) {
                // Create a synthetic event with the cleaned value
                onChange({
                    ...e,
                    target: {
                        ...e.target,
                        value: trimmed,
                    },
                } as React.ChangeEvent<HTMLInputElement>);
            }
        } else {
            if (onChange) onChange(e);
        }
    };
    return (
        <TextFieldWrapper>
            {title && <TextFieldTitle title={title} required={required} />}

            <TextField
                id={inputId}
                variant={variant}
                onChange={handleChange}
                disabled={disabled}
                required={required}
                inputRef={inputRef}
                placeholder={placeholder}
                type={
                    (type === 'password' || showPasswordToggle) && !showPassword
                        ? 'password'
                        : type === 'password' && showPassword
                          ? 'text'
                          : type
                }
                autoComplete={autoComplete || (type === 'password' || showPasswordToggle ? 'new-password' : 'off')}
                size={size}
                error={error}
                name={name}
                value={value}
                autoFocus={isAutoFocus}
                InputProps={{
                    startAdornment: renderStartAdornment(),
                    endAdornment: renderEndAdornment(),
                }}
                slotProps={{
                    htmlInput: {
                        maxLength,
                        inputMode,
                        onKeyDown,
                        ...(errorValue && {
                            'aria-describedby': errorId,
                            'aria-invalid': true,
                        }),
                        ...(ariaLabel && { 'aria-label': ariaLabel }),
                    },
                }}
                onBlur={onBlur}
                onCopy={handleCopy}
                onPaste={handlePaste}
                onCut={handleCut}
                sx={sx}
                fullWidth
                multiline={multiline}
                rows={rows}
                label={label}
                className={className}
            />
            {errorValue && (
                <Typography
                    id={errorId}
                    variant="subtitle2"
                    className="errorTxt"
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    tabIndex={0}
                >
                    {errorValue}
                </Typography>
            )}
            {helperTitle && (
                <Typography
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={helperTitleOnClick}
                    onKeyDown={e => {
                        if (helperTitleOnClick && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            helperTitleOnClick();
                        }
                    }}
                    sx={{ cursor: 'pointer', width: 'fit-content' }}
                    variant="subtitle2"
                    className="helperTitle"
                >
                    {helperTitle}
                </Typography>
            )}
        </TextFieldWrapper>
    );
};
